import { useSyncExternalStore } from 'react';

/**
 * "Are the pedigree keyboard shortcuts on?" — one answer, shared by everything that
 * needs it.
 *
 * The toggle lives behind the keyboard icon in the canvas's floating toolbar and
 * has always been local `useState` in `ChartBox`, persisted to localStorage. That
 * was fine while only `ChartBox` acted on it. It stopped being fine when the add
 * controls began ADVERTISING their bindings (TGCLOUD-1642's footnote): a clinician
 * who has turned shortcuts off should not be shown key caps for keys that do
 * nothing, and the sidebar's Add Relative panel renders from `PedigreeSideBar`, a
 * different subtree entirely — so there was no prop path from the toggle to half
 * the things that depend on it.
 *
 * A module-level value plus `useSyncExternalStore` rather than a Context: the
 * toggle is a single boolean read by three unrelated subtrees, it is already
 * persisted globally, and a provider would have to wrap a common ancestor of the
 * canvas and the sidebar for no other reason.
 *
 * NOTE the localStorage key is the one `ChartBox` has always used, so an existing
 * preference carries over rather than silently resetting to on.
 */

const STORAGE_KEY = 'pedigree-shortcuts-enabled';

// Default ON — only an explicit 'false' turns them off, matching the original read.
const readStored = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) !== 'false';
  } catch {
    // Private-mode / storage-disabled: shortcuts stay on, which is the default.
    return true;
  }
};

let enabled = readStored();
const listeners = new Set();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getShortcutsEnabled = () => enabled;

export const setShortcutsEnabled = (next) => {
  if (next === enabled) return;
  enabled = next;
  try {
    localStorage.setItem(STORAGE_KEY, String(next));
  } catch {
    // Non-persisting is acceptable; the in-memory value is still correct.
  }
  listeners.forEach((listener) => listener());
};

export const toggleShortcutsEnabled = () => setShortcutsEnabled(!enabled);

/** Subscribe a component to the toggle. */
export const useShortcutsEnabled = () =>
  useSyncExternalStore(subscribe, getShortcutsEnabled, getShortcutsEnabled);

/** Test-only: drop back to the stored value and clear subscribers between tests. */
export const __resetShortcutsEnabledStore = () => {
  enabled = readStored();
  listeners.clear();
};
